import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Database, Tag, Loader2, ShieldAlert } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';

export const ReferenceManager = () => {
  const { profile } = useAuth();
  const [activeTable, setActiveTable] = useState<string>('organization_types');
  const queryClient = useQueryClient();

  // Consistent role logic: system_admin and org_manager are considered "Managers"
  const isManager = profile?.role === 'system_admin' || profile?.role === 'org_manager';

  const { data: rows, isLoading } = useQuery({
    queryKey: ['referenceTable', activeTable],
    queryFn: async () => {
      if (activeTable === 'engagement_tag_groups') {
        const { data, error } = await supabase
          .from('engagement_tag_groups')
          .select('*, engagement_tags(*)');
        if (error) return [];
        return data;
      }
      const { data, error } = await supabase.from(activeTable).select('*').order('label');
      if (error) return [];
      return data;
    }
  });

  const addRow = useMutation({
    mutationFn: async ({ label, groupId }: { label: string, groupId?: string }) => { 
      if (!isManager) throw new Error("Unauthorized: Insufficient Role.");
      
      const payload: any = { label };
      let table = activeTable;

      if (activeTable === 'engagement_tag_groups' && groupId) {
        table = 'engagement_tags';
        payload.group_id = groupId;
      }
      
      const { data, error } = await supabase
        .from(table)
        .insert([payload])
        .select(); 
        
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['referenceTable'] }),
    onError: (err: any) => alert(`Registry Update Blocked: ${err.message}`)
  });

  const deleteRow = useMutation({
    mutationFn: async ({ id, table }: { id: string, table?: string }) => { 
      if (!isManager) throw new Error("Unauthorized: Insufficient Role.");
      
      const targetTable = table || activeTable;
      const { data, error } = await supabase
        .from(targetTable)
        .delete()
        .eq('id', id)
        .select(); 
        
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['referenceTable'] }),
    onError: (err: any) => alert(`Registry Deletion Blocked: ${err.message}`)
  });

  const tables = [
    { id: 'organization_types', label: 'ORG TYPES' },
    { id: 'organization_roles', label: 'ORG ROLES' },
    { id: 'affiliations', label: 'AFFILIATIONS' },
    { id: 'session_staff_roles', label: 'STAFF ROLES' },
    { id: 'program_categories', label: 'PROG CATEGORIES' },
    { id: 'engagement_categories', label: 'ENG STRUCTURE' },
    { id: 'engagement_tag_groups', label: 'ENG TAGS' },
    { id: 'audience_types', label: 'AUDIENCE TYPES' }
  ];

  return (
    <div className="flex flex-col">
      <div className="flex bg-slate-50 border-b border-slate-100 p-1 gap-1 overflow-x-auto scrollbar-hide">
        {tables.map(t => (
          <button 
            key={t.id} 
            onClick={() => setActiveTable(t.id)}
            className={`
              px-4 py-2 rounded-md text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap
              ${activeTable === t.id 
                ? 'bg-white text-slate-900 shadow-sm border border-slate-200' 
                : 'text-slate-400 hover:text-slate-600 hover:bg-white/50'}
            `}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-8 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h4 className="text-lg font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
              <Database size={16} className="text-blue-500" /> 
              {tables.find(t => t.id === activeTable)?.label}
            </h4>
          </div>
          {isManager && (
            <button 
              onClick={() => { const l = prompt(`Enter new ${activeTable === 'engagement_tag_groups' ? 'group' : 'label'}:`); if(l) addRow.mutate({ label: l }); }}
              className="flex items-center gap-2 px-4 py-2 bg-[#FFDB00] hover:bg-[#f5d100] text-black rounded-md font-bold uppercase text-[10px] tracking-wider transition-all shadow-sm"
            >
              <Plus size={14} />
              {activeTable === 'engagement_tag_groups' ? 'New Tag Group' : 'Add Entry'}
            </button>
          )}
        </div>

        <div className={activeTable === 'engagement_tag_groups' ? 'space-y-4' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'}>
          {isLoading ? (
            <div className="col-span-full py-12 text-center text-slate-300 font-bold uppercase text-[10px] tracking-widest animate-pulse flex items-center justify-center gap-2">
              <Loader2 className="animate-spin" size={14} /> Syncing records...
            </div>
          ) : rows?.length === 0 ? (
            <div className="col-span-full py-12 text-center flex flex-col items-center gap-2 bg-slate-50 border border-dashed border-slate-200 rounded-md">
              <Tag size={24} className="text-slate-200" />
              <p className="text-slate-300 font-bold uppercase text-[9px] tracking-widest">Registry Empty</p>
            </div>
          ) : activeTable === 'engagement_tag_groups' ? (
            rows?.map((group: any) => (
              <div key={group.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="px-6 py-4 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between">
                  <h5 className="font-black text-slate-900 text-xs uppercase tracking-widest">{group.label}</h5>
                  {isManager && (
                    <button 
                      className="text-slate-300 hover:text-rose-500 transition-colors"
                      onClick={() => { if(confirm(`Delete group "${group.label}" and all its tags?`)) deleteRow.mutate({ id: group.id }); }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <div className="p-6 flex flex-wrap gap-2">
                  {group.engagement_tags?.map((tag: any) => (
                    <div key={tag.id} className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full group/tag">
                      <span className="text-[10px] font-bold text-slate-700 uppercase">{tag.label}</span>
                      {isManager && (
                        <button 
                          onClick={() => { if(confirm('Remove this tag?')) deleteRow.mutate({ id: tag.id, table: 'engagement_tags' }); }}
                          className="text-slate-300 hover:text-rose-500 transition-colors"
                        >
                          <Trash2 size={10} />
                        </button>
                      )}
                    </div>
                  ))}
                  {isManager && (
                    <button 
                      onClick={() => { const l = prompt(`Add tag to ${group.label}:`); if(l) addRow.mutate({ label: l, groupId: group.id }); }}
                      className="px-3 py-1.5 border border-dashed border-green-200 text-green-600 hover:bg-green-50 rounded-full text-[10px] font-bold uppercase flex items-center gap-1.5 transition-all"
                    >
                      <Plus size={12} />
                      Add Option
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            rows?.map((row: any) => (
              <div key={row.id} className="px-4 py-3 bg-white border border-slate-200 rounded-md flex items-center justify-between group hover:border-slate-400 transition-all shadow-sm">
                <span className="font-bold text-[11px] text-slate-900 uppercase tracking-tight">{row.label}</span>
                {isManager && (
                  <button 
                    className="text-slate-200 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-all" 
                    onClick={() => { if(confirm('Wipe registry entry?')) deleteRow.mutate({ id: row.id, table: activeTable }); }}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {!isManager && (
          <div className="p-4 bg-amber-50 border border-amber-100 rounded-lg flex items-center gap-3 text-amber-600">
             <ShieldAlert size={16} />
             <p className="text-[9px] font-black uppercase tracking-widest">Read-Only Mode: You lack the permissions to modify global lookups.</p>
          </div>
        )}
      </div>
    </div>
  );
};